import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { ReleasePost } from 'fym-shared/src/music24/releases';
import { KIT_ASSET_WORKING_STATUSES, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { SerializedReel } from '../../types';
import { kitAssetPicture, type KitPicture } from '../../kitPictureSrc';

/**
 * Releases v2 (design c1, owner answers 1–3): one card is one asset, a type + variant (for example
 * "Story (Out now)"), or one AI reel length. The card shows the newest revision and its number
 * (r1, r2). A re-make is the next revision of the same card. Pure data, so it is tested without React.
 */
export type RevisionCardState = 'ready' | 'queued' | 'working' | 'failed';

export interface RevisionCard {
  /** `kitAsset:<themeId>:<variant>` or `reel:<variant>`. */
  key: string;
  kind: 'kitAsset' | 'reel';
  /** The asset type; `aiReel` for a reel. */
  themeId: StudioThemeId;
  variant: string;
  /** The newest revision (kit asset). */
  asset: SerializedKitAsset | null;
  /** The newest revision (reel). */
  reel: SerializedReel | null;
  /** The ready revision that the card can open while the newest works or failed; null when none. */
  readyAsset: SerializedKitAsset | null;
  readyReel: SerializedReel | null;
  /** The number of the newest revision. */
  revision: number;
  /** The number of the ready revision behind a failed or working newest one; null when none. */
  readyRevision: number | null;
  state: RevisionCardState;
  /** 0..100 while it renders. */
  progress: number;
  /** When the newest revision was made (ISO). */
  createdAt: string;
  releaseThemeId: ReleaseThemeId;
}

const WORKING_REEL: ReadonlyArray<string> = ['queued', 'analysing', 'planning', 'rendering'];
const READY_REEL: ReadonlyArray<string> = ['ready', 'approved'];

function kitState(asset: SerializedKitAsset): RevisionCardState {
  if (asset.status === 'ready') {
    return 'ready';
  }
  if (asset.status === 'failed') {
    return 'failed';
  }
  return asset.status === 'queued' ? 'queued' : 'working';
}

function reelState(reel: SerializedReel): RevisionCardState {
  if (READY_REEL.includes(reel.status)) {
    return 'ready';
  }
  if (reel.status === 'failed') {
    return 'failed';
  }
  return reel.status === 'queued' ? 'queued' : 'working';
}

/** The variant of a reel: `variant`, or its length for an older answer. */
export function reelVariant(reel: SerializedReel): string {
  return reel.variant ?? `${reel.durationSec}s`;
}

function kitCards(newest: SerializedKitAsset[], current: SerializedKitAsset[]): RevisionCard[] {
  const currentByKey = new Map(current.map((asset) => [`${asset.themeId}:${asset.variant}`, asset]));
  return newest.map((asset) => {
    const key = `${asset.themeId}:${asset.variant}`;
    const shown = currentByKey.get(key);
    const ready =
      asset.status === 'ready'
        ? asset
        : shown !== undefined && shown.status === 'ready' && shown.uuid !== asset.uuid
          ? shown
          : null;
    return {
      key: `kitAsset:${key}`,
      kind: 'kitAsset',
      themeId: asset.themeId,
      variant: asset.variant,
      asset,
      reel: null,
      readyAsset: ready,
      readyReel: null,
      revision: asset.version,
      readyRevision: ready === null || ready === asset ? null : ready.version,
      state: kitState(asset),
      progress: KIT_ASSET_WORKING_STATUSES.includes(asset.status) ? asset.progress : 0,
      createdAt: asset.createdAt,
      releaseThemeId: asset.releaseThemeId,
    };
  });
}

function reelCards(reels: SerializedReel[]): RevisionCard[] {
  const byVariant = new Map<string, SerializedReel[]>();
  reels
    .filter((reel) => reel.status !== 'rejected')
    .forEach((reel) => byVariant.set(reelVariant(reel), [...(byVariant.get(reelVariant(reel)) ?? []), reel]));
  return Array.from(byVariant.entries()).map(([variant, list]) => {
    const sorted = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const newest = sorted[0] as SerializedReel;
    const ready = sorted.find((reel) => READY_REEL.includes(reel.status) && reel.video !== null) ?? null;
    const revision = newest.revision ?? sorted.length;
    return {
      key: `reel:${variant}`,
      kind: 'reel',
      themeId: 'aiReel',
      variant,
      asset: null,
      reel: newest,
      readyAsset: null,
      readyReel: ready,
      revision,
      readyRevision: ready === null || ready === newest ? null : (ready.revision ?? null),
      state: reelState(newest),
      progress: WORKING_REEL.includes(newest.status) ? newest.progress : 0,
      createdAt: newest.createdAt,
      releaseThemeId: newest.themeId,
    };
  });
}

/**
 * Every card of a release, newest first: the newest revision of each kit type + variant
 * (`newest`, else `assets` for an older answer) and each reel length.
 */
export function buildRevisionCards(input: {
  assets: SerializedKitAsset[];
  newest?: SerializedKitAsset[];
  reels: SerializedReel[];
}): RevisionCard[] {
  return [...kitCards(input.newest ?? input.assets, input.assets), ...reelCards(input.reels)].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  );
}

/** The base time of "Just made" and "N new": the last view, else 7 days back (owner answer 4). */
export function newSince(lastViewedAt: string | null, now: Date): string {
  return lastViewedAt ?? new Date(now.getTime() - 7 * 86_400_000).toISOString();
}

/** The cards whose newest revision was made after `since`, newest first. */
export function newCards(cards: RevisionCard[], since: string): RevisionCard[] {
  return cards.filter((card) => card.createdAt > since);
}

/**
 * The picture of a card: the poster of a video, else the image file of the ready revision (the
 * newest, else the older ready one), with its imgproxy source (`kitPictureSrc` draws it sized). A
 * reel poster has no source in the answer yet: it falls back to the file route. Null with no ready
 * file.
 */
export function cardPicture(card: RevisionCard): KitPicture | null {
  if (card.kind === 'reel') {
    const reel = card.reel?.video !== null && card.reel?.video !== undefined ? card.reel : card.readyReel;
    const poster = reel?.video?.posterUrl ?? null;
    return poster === null ? null : { url: poster, sourceUrl: null };
  }
  const asset = card.asset?.status === 'ready' ? card.asset : card.readyAsset;
  if (asset === null || asset.fileRemoved === true) {
    return null;
  }
  return kitAssetPicture(asset, asset.durationSec !== null);
}

// ---------------------------------------------------------------- Groups

export type RevisionGroupId =
  | 'stories'
  | 'feedPosts'
  | 'lyricCards'
  | 'coverSizes'
  | 'profile'
  | 'shortVideos'
  | 'fullVideos'
  | 'aiReels';

/** The groups of the Assets tab and of "Content made", in page order (design b1, c1). */
export const REVISION_GROUPS: ReadonlyArray<{ id: RevisionGroupId; themes: ReadonlyArray<StudioThemeId> }> = [
  { id: 'stories', themes: ['story'] },
  { id: 'feedPosts', themes: ['feedPost'] },
  { id: 'lyricCards', themes: ['lyricCard'] },
  { id: 'coverSizes', themes: ['coverSizes'] },
  { id: 'profile', themes: ['banners', 'spotifyHeader', 'linkPreview', 'thumbnail'] },
  { id: 'shortVideos', themes: ['artworkInMotion', 'countdown', 'canvasLoop', 'lyricMoment', 'motionArtwork'] },
  { id: 'fullVideos', themes: ['lyricVideo', 'visualizer', 'musicVideo'] },
  { id: 'aiReels', themes: ['aiReel'] },
];

/** The format filter of a group (design c1 chips: Images, Short videos, Full videos, AI reels). */
export type RevisionFormatFilter = 'all' | 'image' | 'short' | 'full' | 'aiReel';

export interface RevisionGroup {
  id: RevisionGroupId;
  cards: RevisionCard[];
}

/** The cards by group, in page order; a group with no card is left out. */
export function groupRevisionCards(cards: RevisionCard[]): RevisionGroup[] {
  return REVISION_GROUPS.map((group) => ({
    id: group.id,
    cards: cards.filter((card) => group.themes.includes(card.themeId)),
  })).filter((group) => group.cards.length > 0);
}

// ---------------------------------------------------------------- Post state

export type CardPostState = 'posted' | 'scheduled' | null;

/**
 * "Posted" when a posted post uses the newest revision, else "Scheduled" when a planned post uses
 * it (design c1). The release posts carry the kit asset or the reel of their media file.
 */
export function cardPostState(card: RevisionCard, posts: ReleasePost[]): CardPostState {
  const uuid = card.asset?.uuid ?? card.reel?.uuid;
  const mine = posts.filter((post) => (post.kitAsset?.uuid ?? post.reel?.uuid) === uuid);
  if (mine.some((post) => post.status === 'posted')) {
    return 'posted';
  }
  return mine.some((post) => post.status !== 'failed' && post.status !== 'canceled') ? 'scheduled' : null;
}
